import { motion } from "framer-motion";
import { Reveal } from "@/components/common/Reveal";
import { Tractor, Heart, Trophy, Droplets, Compass, PenLine } from "lucide-react";

const THEMES = [
    {
        icon: Tractor,
        title: "Agriculture & Bâtiments agricoles",
        desc: "Conception des bâtiments agricoles, nouvelles normes d'élevage, ventilation naturelle, évolution des exploitations, aménagements fonctionnels et innovations agricoles.",
        articles: [
            "Comment concevoir une stabulation performante ?",
            "Les erreurs à éviter lors d'une extension de bâtiment agricole.",
            "Comment préparer son exploitation aux défis climatiques ?",
        ],
    },
    {
        icon: Heart,
        title: "Bien-être animal & zootechnie",
        desc: "Parce que le confort animal influence directement les performances de l'exploitation : comportement des bovins, organisation des bâtiments, aires de couchage, circulations, abreuvement et qualité de l'ambiance intérieure.",
        articles: [
            "Quels sont les principes d'une circulation animale efficace ?",
            "Comment concevoir un bâtiment autour du comportement naturel des animaux ?",
            "Pourquoi les vaches choisissent-elles certaines zones du bâtiment ?",
        ],
    },
    {
        icon: Trophy,
        title: "Haras & infrastructures équestres",
        desc: "Conception et organisation des écuries, manèges et haras, pensés autour du cheval et de son cavalier.",
        articles: [
            "Comment concevoir une écurie fonctionnelle ?",
            "Quels sont les fondamentaux d'un haras bien organisé ?",
            "Quelle est l'importance du paysage dans les projets équestres ?",
        ],
    },
    {
        icon: Droplets,
        title: "Paysage, Eau & Territoire",
        desc: "Le paysage et l'eau comme éléments structurants d'un projet agricole durable.",
        articles: [
            "Pourquoi les haies redeviennent stratégiques ?",
            "L'eau, premier élément de conception d'un projet agricole.",
            "Pourquoi créer un paysage utile à l'exploitation ?",
        ],
    },
    {
        icon: Compass,
        title: "Géobiologie & Feng Shui",
        desc: "Lire et comprendre l'identité d'un lieu avant d'intervenir, à la manière des anciens bâtisseurs.",
        articles: [
            "Pourquoi observer le terrain avant de construire ?",
            "Les animaux perçoivent-ils leur environnement différemment ?",
            "Pourquoi comprendre l'identité d'un lieu avant d'intervenir ?",
        ],
    },
];

const ArticleCard = ({ title }) => (
    <article
        data-testid="blog-article-card"
        className="group flex h-full cursor-default flex-col justify-between rounded-2xl border border-black/5 bg-white p-6 transition-all duration-500 hover:-translate-y-1 hover:shadow-lg hover:shadow-rz-green/5"
    >
        <h4 className="font-display text-lg font-bold leading-snug text-rz-ink">
            {title}
        </h4>
        <span className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-rz-pale/30 px-3.5 py-1.5 text-xs font-semibold tracking-wide text-rz-green">
            <PenLine size={13} /> En cours de rédaction
        </span>
    </article>
);

const Theme = ({ theme }) => (
    <div data-testid="blog-theme" className="border-t border-black/10 pt-12">
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-4">
                <Reveal>
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-rz-green text-white">
                        <theme.icon size={22} />
                    </span>
                    <h3 className="font-display mt-5 text-[clamp(1.5rem,2.5vw,2rem)] font-bold tracking-tight leading-tight text-rz-ink">
                        {theme.title}
                    </h3>
                    <p className="mt-4 text-sm leading-relaxed text-rz-ink-soft">
                        {theme.desc}
                    </p>
                </Reveal>
            </div>
            <div className="lg:col-span-8">
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {theme.articles.map((a, i) => (
                        <Reveal key={a} delay={i * 0.06}>
                            <ArticleCard title={a} />
                        </Reveal>
                    ))}
                </div>
            </div>
        </div>
    </div>
);

export default function Blog() {
    return (
        <main data-testid="blog-page">
            <section className="relative flex min-h-[60svh] items-end overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="/images/blog-hero.jpg"
                        alt="Champ verdoyant à la lumière dorée du soir"
                        className="h-full w-full object-cover rz-hero-zoom"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-rz-deep/55 via-rz-deep/45 to-rz-deep/85" />
                </div>
                <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 md:px-12 pb-14 md:pb-20 pt-32">
                    <motion.span
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.2 }}
                        className="text-xs md:text-sm tracking-[0.25em] uppercase text-rz-pale"
                    >
                        Blog & Actualités
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 26 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.32, ease: [0.16, 1, 0.3, 1] }}
                        className="font-display mt-5 max-w-4xl text-white font-extrabold tracking-tight leading-[1.02] text-[clamp(2.2rem,5.5vw,4.4rem)] [text-shadow:_0_2px_30px_rgba(10,26,18,0.45)]"
                    >
                        Comprendre aujourd'hui pour construire demain.
                    </motion.h1>
                </div>
            </section>

            <section className="bg-rz-bg py-24 lg:py-32 px-6 md:px-12">
                <div className="mx-auto max-w-[1400px]">
                    <div className="mx-auto max-w-3xl text-center mb-20">
                        <Reveal>
                            <p className="text-base lg:text-lg leading-relaxed text-rz-ink-soft">
                                L'agriculture, l'élevage, l'énergie et le monde équestre évoluent en
                                permanence. À travers notre blog, nous partageons analyses, retours
                                d'expérience, conseils techniques et réflexions de terrain pour
                                accompagner les exploitants, les éleveurs, les propriétaires de haras
                                et les porteurs de projets.
                            </p>
                        </Reveal>
                        <Reveal delay={0.12}>
                            <span className="mx-auto mt-8 block h-px w-16 bg-rz-sage/60" />
                            <p className="mt-8 font-display text-xl lg:text-2xl font-bold leading-snug text-rz-ink">
                                Notre objectif est simple : transmettre des connaissances utiles,
                                concrètes et applicables.
                            </p>
                        </Reveal>
                    </div>

                    <div className="space-y-16 lg:space-y-20">
                        {THEMES.map((t) => (
                            <Theme key={t.title} theme={t} />
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
}
